/**
 * Import Sources Management Page
 * Phase 7: Manage automated import sources (S3, SFTP, webhooks)
 */

import { useState } from 'react';
import { TopBar } from '@/components/layout/TopBar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useImportSources,
  useCreateImportSource,
  useUpdateImportSource,
  useDeleteImportSource,
  useToggleImportSource,
  SOURCE_TYPE_CONFIG,
  type ImportSourceType,
} from '@/hooks/useImportSources';
import {
  FileSpreadsheet,
  Server,
  Webhook,
  Plus,
  MoreHorizontal,
  Edit,
  Trash,
  History,
  CheckCircle2,
  XCircle,
  Loader2,
  Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import type { Database } from '@/integrations/supabase/types';

type ImportSource = Database['public']['Tables']['import_sources']['Row'];

const SOURCE_TYPE_ICONS: Record<string, React.ReactNode> = {
  s3_csv: <FileSpreadsheet className="h-4 w-4 text-orange-500" />,
  sftp_csv: <Server className="h-4 w-4 text-blue-500" />,
  api_webhook: <Webhook className="h-4 w-4 text-purple-500" />,
};

export default function ImportSources() {
  const navigate = useNavigate();
  const { data: sources, isLoading } = useImportSources();
  const createSource = useCreateImportSource();
  const updateSource = useUpdateImportSource();
  const deleteSource = useDeleteImportSource();
  const toggleSource = useToggleImportSource();

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editSource, setEditSource] = useState<ImportSource | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    source_type: 's3_csv' as ImportSourceType,
    connection_config: {} as Record<string, string>,
    poll_interval_minutes: 60,
  });

  const resetForm = () => {
    setFormData({
      name: '',
      source_type: 's3_csv',
      connection_config: {},
      poll_interval_minutes: 60,
    });
    setEditSource(null);
  };

  const handleOpenAdd = () => {
    resetForm();
    setShowAddDialog(true);
  };

  const handleOpenEdit = (source: ImportSource) => {
    setEditSource(source);
    setFormData({
      name: source.name,
      source_type: source.source_type as ImportSourceType,
      connection_config: (source.config || {}) as Record<string, string>,
      poll_interval_minutes: source.poll_interval_minutes || 60,
    });
    setShowAddDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editSource) {
        await updateSource.mutateAsync({
          id: editSource.id,
          name: formData.name,
          source_type: formData.source_type,
          config: formData.connection_config,
          poll_interval_minutes: formData.poll_interval_minutes,
        });
        toast.success('Source updated');
      } else {
        await createSource.mutateAsync({
          name: formData.name,
          source_type: formData.source_type,
          config: formData.connection_config,
          poll_interval_minutes: formData.poll_interval_minutes,
        });
        toast.success('Source created');
      }
      setShowAddDialog(false);
      resetForm();
    } catch (error) {
      toast.error('Failed to save source');
    }
  };

  const handleDelete = async (sourceId: string) => {
    if (!confirm('Delete this import source? This cannot be undone.')) return;
    try {
      await deleteSource.mutateAsync(sourceId);
      toast.success('Source deleted');
    } catch (error) {
      toast.error('Failed to delete source');
    }
  };

  const handleToggle = async (source: ImportSource, is_active: boolean) => {
    try {
      await toggleSource.mutateAsync({ id: source.id, is_active });
      toast.success(`Source ${is_active ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update source');
    }
  };

  const currentConfig = SOURCE_TYPE_CONFIG[formData.source_type];

  return (
    <div className="min-h-screen bg-background">
      <TopBar />

      <div className="container max-w-5xl mx-auto py-6 px-4 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Import Sources</h1>
            <p className="text-muted-foreground">
              Configure automated data imports from external systems
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/import-runs')}>
              <History className="h-4 w-4 mr-2" />
              View Runs
            </Button>
            <Button onClick={handleOpenAdd}>
              <Plus className="h-4 w-4 mr-2" />
              Add Source
            </Button>
          </div>
        </div>

        {/* Sources Table */}
        <Card>
          <CardHeader>
            <CardTitle>Configured Sources</CardTitle>
            <CardDescription>
              Sources are polled automatically based on their interval settings
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : !sources?.length ? (
              <div className="text-center py-12">
                <FileSpreadsheet className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">No import sources configured</p>
                <Button onClick={handleOpenAdd}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Source
                </Button>
              </div>
            ) : (
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Source</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Interval</TableHead>
                      <TableHead>Last Poll</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Enabled</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sources.map((source) => (
                      <TableRow key={source.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {SOURCE_TYPE_ICONS[source.source_type] || <FileSpreadsheet className="h-4 w-4" />}
                            {source.name}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {SOURCE_TYPE_CONFIG[source.source_type as ImportSourceType]?.label || source.source_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {source.poll_interval_minutes}m
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {source.last_polled_at
                            ? formatDistanceToNow(new Date(source.last_polled_at), { addSuffix: true })
                            : 'Never'}
                        </TableCell>
                        <TableCell>
                          {source.last_successful_at ? (
                            <Badge variant="secondary" className="gap-1 bg-green-500/10 text-green-600">
                              <CheckCircle2 className="h-3 w-3" />
                              OK
                            </Badge>
                          ) : source.last_polled_at ? (
                            <Badge variant="destructive" className="gap-1">
                              <XCircle className="h-3 w-3" />
                              Error
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Pending</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={source.is_active ?? false}
                            onCheckedChange={(checked) => handleToggle(source, checked)}
                          />
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/import-runs?source=${source.id}`)}>
                                <History className="h-4 w-4 mr-2" />
                                View Runs
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleOpenEdit(source)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => handleDelete(source.id)}
                              >
                                <Trash className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editSource ? 'Edit Import Source' : 'Add Import Source'}</DialogTitle>
            <DialogDescription>
              Configure an automated import source for ticket data
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Protractor S3 Feed"
              />
            </div>

            <div className="space-y-2">
              <Label>Source Type</Label>
              <Select
                value={formData.source_type}
                onValueChange={(val) =>
                  setFormData((prev) => ({
                    ...prev,
                    source_type: val as ImportSourceType,
                    connection_config: {},
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SOURCE_TYPE_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        {SOURCE_TYPE_ICONS[key]}
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{currentConfig?.description}</p>
            </div>

            <div className="space-y-2">
              <Label>Poll Interval (minutes)</Label>
              <Select
                value={String(formData.poll_interval_minutes)}
                onValueChange={(val) =>
                  setFormData((prev) => ({ ...prev, poll_interval_minutes: parseInt(val) }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">Every 15 minutes</SelectItem>
                  <SelectItem value="30">Every 30 minutes</SelectItem>
                  <SelectItem value="60">Every hour</SelectItem>
                  <SelectItem value="180">Every 3 hours</SelectItem>
                  <SelectItem value="360">Every 6 hours</SelectItem>
                  <SelectItem value="1440">Daily</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Dynamic connection fields based on source type */}
            {currentConfig?.fields.map((field) => (
              <div key={field} className="space-y-2">
                <Label htmlFor={field}>{field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</Label>
                <Input
                  id={field}
                  value={formData.connection_config[field] || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      connection_config: { ...prev.connection_config, [field]: e.target.value },
                    }))
                  }
                  placeholder={`Enter ${field.replace(/_/g, ' ')}`}
                />
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!formData.name || createSource.isPending || updateSource.isPending}
            >
              {(createSource.isPending || updateSource.isPending) && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {editSource ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
